import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Modal, Stack } from 'react-bootstrap';
import { Trans } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { isAxiosError } from 'axios';
import Sentry from 'src/services/sentry';

import useRemoteBoundStore from 'src/store/useRemoteBoundStore';

import CustomModal from 'src/components/custom_components/CustomModal/CustomModal';

import { Api } from 'src/models/Api.model';
import { GlobalStatus } from 'src/models/GalenData/DeviceDataView.model';
import { defaultBackendStatus } from 'src/models/InitialBackendStatus';
import { getLocaleCode, Language } from 'src/models/Language.model';
import { getModels } from 'src/models/Product.model';
import { EyesPosition, Session } from 'src/models/Session.model';

import useGetSessions from 'src/hooks/useGetSessions';
import useUiTranslation from 'src/hooks/useUiTranslation';

import getGradingResult from 'src/utils/getGradingResult';

import ProgressModalBody from './ProgressModalBody';
import ProgressModalFooter from './ProgressModalFooter';

type AnalyzingProgressModalProps = {
  handleRetake: (position: EyesPosition) => void;
  handleCloseAnalyzingProgressModal: () => void;
  session: Session;
  handleResetSession: () => void;
  resubmitExistingSession: (iid: string) => void;
};

export default function AnalyzingProgressModal({
  handleRetake,
  handleCloseAnalyzingProgressModal,
  session,
  handleResetSession,
  resubmitExistingSession,
}: AnalyzingProgressModalProps) {
  const location = useLocation<{
    isResume: boolean;
  }>();

  const {
    practiceSettings: { product, camera },
    backendStatus,
    setBackendStatus,
    totalLeftImageAttempt,
    totalRightImageAttempt,
    isAnalyzing,
    setIsAnalyzing,
    updateSession,
  } = useRemoteBoundStore();

  const models = getModels(product, camera, session.template);

  const refreshCountRef = useRef(0);

  const refreshTime = 500;
  const maxTime = 120;

  const { tPages, tCommon, language } = useUiTranslation();

  const { getSessions } = useGetSessions();

  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const getNewSession = useCallback(
    async (deviceDataId: string) => {
      try {
        const newSession = await Api.remote.load_session({
          deviceDataId,
        });
        await getSessions(newSession.iid);
        updateSession(newSession);
      } catch (error) {
        if (isAxiosError(error) && error.response?.status === 404) {
          return;
        }
        throw error;
      }
    },
    [getSessions, updateSession],
  );

  useEffect(() => {
    if (!isAnalyzing) {
      return;
    }

    // Clear any existing interval before starting a new one
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    const fetchBackendState = async () => {
      try {
        refreshCountRef.current += 1;

        if (refreshCountRef.current * refreshTime === maxTime * 1000) {
          Api.confirmBox({
            title: tCommon('bootbox.title.Error'),
            message: tCommon('message.error.examSubmissionTimeout'),
            buttons: {
              confirm: {
                label: tPages('newExam.resubmit'),
                className: 'btn-primary',
              },
              cancel: {
                label: tPages('newExam.continueWaiting'),
              },
            },
            callback: (result: boolean) => {
              refreshCountRef.current = 0;
              if (result) resubmitExistingSession(session.iid);
            },
            locale: getLocaleCode(language as Language),
          });

          Sentry.captureMessage(
            `Exam submission timed out within ${(refreshCountRef.current * refreshTime) / 1000} seconds of ${maxTime} seconds for deviceDataId: ${session.iid}`,
          );
        }

        const BackendStatusData = await Api.remote.get_backend_state(
          session.iid,
        );

        if (!BackendStatusData) {
          return;
        }
        const backendStatus = BackendStatusData;

        if (BackendStatusData.GlobalStatus.value !== GlobalStatus.ReportEnd) {
          setBackendStatus(backendStatus);
        }

        if (BackendStatusData.GlobalStatus.value === GlobalStatus.ReportEnd) {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          setIsAnalyzing(false);
          refreshCountRef.current = 0;
          await getNewSession(session.iid);
          setBackendStatus(backendStatus);
        }
      } catch (error) {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        setIsAnalyzing(false);
        refreshCountRef.current = 0;

        Api.alertBox({
          title: tCommon('bootbox.title.Error'),
          message: tCommon('message.error.server-error'),
          locale: getLocaleCode(language as Language),
          type: 'error',
        });
        Sentry.captureException(error);
      }
    };

    intervalRef.current = setInterval(fetchBackendState, refreshTime);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
    // Stops new fetches to be created indefinitely
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAnalyzing, session.iid]);

  const totalOfImagingEyes = useMemo(
    () =>
      location.state?.isResume
        ? (session.leftImageFile ? 1 : 0) + (session.rightImageFile ? 1 : 0)
        : 2,
    [location.state?.isResume, session.leftImageFile, session.rightImageFile],
  );

  const { totalGradableEyes } = getGradingResult(session);

  const hasCompletedExam = totalOfImagingEyes === totalGradableEyes;

  const shouldShowDilationRecommendation =
    (totalLeftImageAttempt >= 3 || totalRightImageAttempt >= 3) &&
    totalLeftImageAttempt < 6 &&
    totalRightImageAttempt < 6 &&
    !hasCompletedExam;

  const shouldShowReachMaxRecommendedAttempts =
    totalLeftImageAttempt >= 6 || totalRightImageAttempt >= 6;

  return (
    <CustomModal
      show={true}
      onHide={handleCloseAnalyzingProgressModal}
      size="lg"
    >
      <Modal.Header>
        <Stack>
          <Modal.Title>{tPages('newExam.analyzingFundusImages')}</Modal.Title>

          <Trans
            i18nKey="ui.pages.newExam.imagingAttempts"
            values={{ totalLeftImageAttempt, totalRightImageAttempt }}
            components={[
              <p className="mt-2 mb-0">
                {totalLeftImageAttempt} {totalRightImageAttempt}
              </p>,
            ]}
          />
        </Stack>
      </Modal.Header>
      <ProgressModalBody
        session={session}
        shouldShowDilationRecommendation={shouldShowDilationRecommendation}
        shouldShowReachMaxRecommendedAttempts={
          shouldShowReachMaxRecommendedAttempts
        }
        status={backendStatus ?? defaultBackendStatus}
        models={models}
      />
      <ProgressModalFooter
        handleRetake={handleRetake}
        hasCompletedExam={hasCompletedExam}
        shouldShowDilationRecommendation={shouldShowDilationRecommendation}
        shouldShowReachMaxRecommendedAttempts={
          shouldShowReachMaxRecommendedAttempts
        }
        handleCloseAnalyzingProgressModal={handleCloseAnalyzingProgressModal}
        handleResetSession={handleResetSession}
      />
    </CustomModal>
  );
}
